/**
 * Deal Intelligence Dashboard - Formatting Utilities
 * European currency formatting and other display utilities
 */

/**
 * Format number as European currency (e.g., 1.250.000,00 €)
 * @param {number} value - The number to format
 * @param {boolean} showDecimals - Whether to show decimal places
 * @param {boolean} showSymbol - Whether to show € symbol
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, showDecimals = false, showSymbol = true) => {
  if (value === null || value === undefined || isNaN(value)) {
    return showSymbol ? '— €' : '—';
  }

  const formatter = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  });

  const formatted = formatter.format(value);
  return showSymbol ? `${formatted} €` : formatted;
};

/**
 * Format number as compact currency (e.g., 1,25M €)
 * @param {number} value - The number to format
 * @returns {string} Compact formatted currency
 */
export const formatCurrencyCompact = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '— €';
  }

  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(2).replace('.', ',')}M €`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(0)}K €`;
  }
  return formatCurrency(value);
};

/**
 * Format percentage with proper sign
 * @param {number} value - The percentage value
 * @param {number} decimals - Number of decimal places
 * @param {boolean} showSign - Show + for positive values
 * @returns {string} Formatted percentage
 */
export const formatPercent = (value, decimals = 1, showSign = false) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '— %';
  }

  const sign = showSign && value > 0 ? '+' : '';
  return `${sign}${value.toFixed(decimals).replace('.', ',')} %`;
};

/**
 * Format square meters
 * @param {number} value - The area in m²
 * @returns {string} Formatted area
 */
export const formatArea = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '— m²';
  }
  return `${Math.round(value)} m²`;
};

/**
 * Format price per square meter
 * @param {number} value - Price per m²
 * @returns {string} Formatted price per m²
 */
export const formatPricePerSqm = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '— €/m²';
  }

  const formatter = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return `${formatter.format(value)} €/m²`;
};

/**
 * Format date for display
 * @param {string|Date} date - Date to format
 * @param {boolean} includeTime - Include time in output
 * @returns {string} Formatted date
 */
export const formatDate = (date, includeTime = false) => {
  if (!date) return '—';

  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(d.getTime())) return '—';

  const options = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  };

  if (includeTime) {
    options.hour = '2-digit';
    options.minute = '2-digit';
  }

  return d.toLocaleDateString('en-GB', options);
};

/**
 * Format relative time (e.g., "il y a 2 jours")
 * @param {string|Date} date - Date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return '—';

  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Aujourd\'hui';
  if (diffDays === 1) return 'Hier';
  if (diffDays < 7) return `Il y a ${diffDays} jours`;
  if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} semaines`;
  if (diffDays < 365) return `Il y a ${Math.floor(diffDays / 30)} mois`;
  return `Il y a ${Math.floor(diffDays / 365)} ans`;
};

/**
 * Phase 3 DealStage enum - structured pipeline workflow
 * Maps to backend DealStatus enum
 */
export const DEAL_STAGES = {
  SOURCING: 'sourcing',
  INITIAL_SCREENING: 'initial_screening',
  UNDERWRITING: 'underwriting',
  OFFER_MADE: 'offer_made',
  UNDER_NEGOTIATION: 'under_negotiation',
  ACQUIRED: 'acquired',
  RENOVATION: 'renovation',
  READY_FOR_SALE: 'ready_for_sale',
  SOLD: 'sold',
  DEAD: 'dead',
};

/**
 * Get status display config
 * @param {string} status - Deal status (DealStage enum value)
 * @returns {object} Display configuration with label, color, icon, and order
 */
export const getStatusConfig = (status) => {
  const configs = {
    sourcing: { 
      label: 'Sourcing', 
      color: 'bg-slate-100 text-slate-700 border-slate-200',
      icon: 'MagnifyingGlass',
      order: 1,
    },
    initial_screening: { 
      label: 'Analyse Initiale', 
      color: 'bg-sky-50 text-sky-700 border-sky-200',
      icon: 'ListChecks',
      order: 2,
    },
    underwriting: { 
      label: 'Souscription', 
      color: 'bg-blue-50 text-blue-700 border-blue-200',
      icon: 'Calculator',
      order: 3,
    },
    offer_made: { 
      label: 'Offre Faite', 
      color: 'bg-violet-50 text-violet-700 border-violet-200',
      icon: 'PaperPlaneTilt',
      order: 4,
    },
    under_negotiation: { 
      label: 'En Négociation', 
      color: 'bg-amber-50 text-amber-700 border-amber-200',
      icon: 'Handshake',
      order: 5,
    },
    acquired: { 
      label: 'Acquis', 
      color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      icon: 'CheckCircle',
      order: 6,
    },
    renovation: { 
      label: 'Rénovation', 
      color: 'bg-orange-50 text-orange-700 border-orange-200',
      icon: 'Wrench',
      order: 7,
    },
    ready_for_sale: { 
      label: 'Prêt à Vendre', 
      color: 'bg-cyan-50 text-cyan-700 border-cyan-200',
      icon: 'Storefront',
      order: 8,
    },
    sold: { 
      label: 'Vendu', 
      color: 'bg-green-100 text-green-700 border-green-200',
      icon: 'CurrencyEur',
      order: 9,
    },
    dead: { 
      label: 'Abandonné', 
      color: 'bg-red-50 text-red-600 border-red-200',
      icon: 'X',
      order: 10,
    },
  };

  return configs[status] || { label: status, color: 'bg-stone-100 text-stone-700', order: 99 };
};

/**
 * Get all status options for filter dropdowns
 * @returns {Array} Array of status options sorted by pipeline order
 */
export const getStatusOptions = () => {
  return Object.entries(DEAL_STAGES)
    .map(([key, value]) => ({
      value,
      label: getStatusConfig(value).label,
      order: getStatusConfig(value).order,
    }))
    .sort((a, b) => a.order - b.order);
};

/**
 * Check if deal is active (not completed or dead)
 * @param {string} status - Deal status
 * @returns {boolean} Whether the deal is active
 */
export const isActiveDeal = (status) => {
  return !['sold', 'dead'].includes(status);
};

/**
 * Check if deal is in negotiation phase (hot deals)
 * @param {string} status - Deal status
 * @returns {boolean} Whether the deal is in negotiation
 */
export const isNegotiationDeal = (status) => {
  return ['offer_made', 'under_negotiation'].includes(status);
};

/**
 * Get risk level config
 * @param {string} riskLevel - Risk level (low, medium, high, critical)
 * @param {number} riskScore - Risk score (0-100)
 * @returns {object} Risk display configuration
 */
export const getRiskConfig = (riskLevel, riskScore = null) => {
  const configs = {
    low: { label: 'Risque Faible', color: 'text-emerald-600 bg-emerald-50', icon: 'ShieldCheck' },
    medium: { label: 'Risque Moyen', color: 'text-amber-600 bg-amber-50', icon: 'Warning' },
    high: { label: 'Risque Élevé', color: 'text-orange-600 bg-orange-50', icon: 'WarningOctagon' },
    critical: { label: 'Risque Critique', color: 'text-red-600 bg-red-50', icon: 'Skull' },
  };

  // If we have a risk score but no level, derive level
  if (riskScore !== null && !riskLevel) {
    if (riskScore <= 15) riskLevel = 'low';
    else if (riskScore <= 35) riskLevel = 'medium';
    else if (riskScore <= 60) riskLevel = 'high';
    else riskLevel = 'critical';
  }

  return configs[riskLevel] || configs.low;
};

/**
 * Get ROI color class based on value
 * @param {number} roi - ROI percentage
 * @returns {string} Tailwind color class
 */
export const getRoiColorClass = (roi) => {
  if (roi === null || roi === undefined) return 'text-stone-500';
  if (roi >= 25) return 'text-emerald-600';
  if (roi >= 15) return 'text-green-600';
  if (roi >= 10) return 'text-lime-600';
  if (roi >= 0) return 'text-amber-600';
  return 'text-red-600';
};

/**
 * Get profit color class based on value
 * @param {number} profit - Profit value
 * @returns {string} Tailwind color class
 */
export const getProfitColorClass = (profit) => {
  if (profit === null || profit === undefined) return 'text-stone-500';
  if (profit > 0) return 'text-emerald-600';
  if (profit === 0) return 'text-stone-500';
  return 'text-red-600';
};

/**
 * Get severity color for risk flags
 * @param {string} severity - Risk severity level
 * @returns {string} Tailwind color class
 */
export const getSeverityColor = (severity) => {
  const colors = {
    low: 'bg-blue-50 text-blue-700 border-blue-200',
    medium: 'bg-amber-50 text-amber-700 border-amber-200',
    high: 'bg-orange-50 text-orange-700 border-orange-200',
    critical: 'bg-red-50 text-red-700 border-red-200',
  };
  return colors[severity] || colors.low;
};

/**
 * Get source type display config
 * @param {string} sourceType - Source type
 * @returns {object} Display configuration
 */
export const getSourceTypeConfig = (sourceType) => {
  const configs = {
    off_market: { label: 'Hors-Marché', icon: 'key' },
    broker: { label: 'Courtier', icon: 'user' },
    notary: { label: 'Notaire', icon: 'file' },
    direct_owner: { label: 'Propriétaire Direct', icon: 'home' },
    auction: { label: 'Enchères', icon: 'gavel' },
    other: { label: 'Autre', icon: 'circle' },
  };

  return configs[sourceType] || { label: sourceType, icon: 'circle' };
};

/**
 * Format number with thousands separator
 * @param {number} value - Number to format
 * @returns {string} Formatted number
 */
export const formatNumber = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '—';
  }

  return new Intl.NumberFormat('de-DE').format(value);
};

/**
 * Calculate and format duration between dates
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date (defaults to now)
 * @returns {string} Duration string
 */
export const formatDuration = (startDate, endDate = new Date()) => {
  if (!startDate) return '—';

  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;

  const diffMs = end - start;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffMonths = Math.floor(diffDays / 30);

  if (diffMonths > 0) {
    const remainingDays = diffDays % 30;
    return remainingDays > 0 
      ? `${diffMonths}mo ${remainingDays}d`
      : `${diffMonths} months`;
  }
  
  return `${diffDays} days`;
};

/**
 * Format a date as a relative time string (e.g., "il y a 2 heures", "il y a 3 jours")
 */
export const formatRelativeDate = (dateInput) => {
  if (!dateInput) return null;
  
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return null;
  
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);
  
  if (diffSec < 60) {
    return 'À l\'instant';
  }
  if (diffMin < 60) {
    return `Il y a ${diffMin} minute${diffMin === 1 ? '' : 's'}`;
  }
  if (diffHours < 24) {
    return `Il y a ${diffHours} heure${diffHours === 1 ? '' : 's'}`;
  }
  if (diffDays < 7) {
    return `Il y a ${diffDays} jour${diffDays === 1 ? '' : 's'}`;
  }
  if (diffWeeks < 4) {
    return `Il y a ${diffWeeks} semaine${diffWeeks === 1 ? '' : 's'}`;
  }
  if (diffMonths < 12) {
    return `Il y a ${diffMonths} mois`;
  }
  return `Il y a ${diffYears} an${diffYears === 1 ? '' : 's'}`;
};
